import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import { Dispatch, useRef, useState } from "react";
import { FaUpload } from "react-icons/fa";
import { makeStyles, styled } from "@mui/material/styles";
import { fileToText } from "../../../utils/fileUtils";
import { isValidNsnOrCc1 } from "../../../utils/genericUtils";
import {
  FileUploadItem,
  FileUploadErrorItem,
  MAX_UPLOADED_TNS,
} from "./NumberNameRequest";
import { useAppDispatch } from "../../../store/hooks";
import { numberNameActions } from "../../../store/numberNameSlice-TODO";
import TextboxInput from "../../../components/forms/TextboxInput";

interface ResidentialEntriesInterface {
  fileUploadItems: FileUploadItem[];
  setFileUploadItems: Dispatch<React.SetStateAction<FileUploadItem[]>>;
  fileUploadErrorItems: FileUploadErrorItem[];
  setFileUploadErrorItems: Dispatch<
    React.SetStateAction<FileUploadErrorItem[]>
  >;
  comment: string;
  setComment: Dispatch<React.SetStateAction<string>>;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const FileUploadForm = ({
  fileUploadItems,
  setFileUploadItems,
  fileUploadErrorItems,
  setFileUploadErrorItems,
  comment,
  setComment,
}: ResidentialEntriesInterface) => {
  const dispatch = useAppDispatch();
  const [fileName, setFileName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: any) => {
    dispatch(numberNameActions.setIsLoading(true));

    try {
      let file;
      if (event.target && event.target.files && event.target.files.length > 0) {
        file = Array.prototype.slice.call(event.target.files)[0];
        setFileName(file.name);
      } else {
        // They did not select a file
        dispatch(numberNameActions.setIsLoading(false));
        return;
      }

      if (!file.name.endsWith(".csv")) {
        setFileUploadErrorItems([
          {
            phoneNumber: "",
            cnam: "",
            errorMessage: "The file must be a CSV",
            lineIndex: -1,
          },
        ]);
        return;
      }

      const fileText = await fileToText(file);
      let lines = fileText.split("\n").slice(1);

      // Filter out any empty lines
      lines = lines.filter((line) => line && line.trim() !== "");

      // If the file has more than 1,000 lines, just reject
      if (lines.length > MAX_UPLOADED_TNS) {
        setFileUploadErrorItems([
          {
            phoneNumber: "",
            cnam: "",
            errorMessage: `Uploaded file had too many updates (Found ${lines.length}). Maximum is ${MAX_UPLOADED_TNS}`,
            lineIndex: 0,
          },
        ]);
        dispatch(numberNameActions.setIsLoading(false));
        return;
      }

      let uploadItems: FileUploadItem[] = [];
      let errorUploadItems: FileUploadErrorItem[] = [];

      // They ARE allowed to submit commas if the comma is wrapped in quotes. This regex will NOT split on a comma in between quotes.
      const REGEX_COMMA_NOT_IN_QUOTES = /,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/;

      lines.forEach((line, ind) => {
        if (line.split(REGEX_COMMA_NOT_IN_QUOTES).length < 2) {
          errorUploadItems.push({
            phoneNumber: "",
            cnam: "",
            errorMessage: `Line found did not have required 2 fields. "${line}"`,
            lineIndex: ind,
          });
          return;
        }

        if (line.split(REGEX_COMMA_NOT_IN_QUOTES).length > 2) {
          errorUploadItems.push({
            phoneNumber: "",
            cnam: "",
            errorMessage: `Line found had more than required 2 fields. "${line}"`,
            lineIndex: ind,
          });
          return;
        }

        let [tn, cnam] = line.split(REGEX_COMMA_NOT_IN_QUOTES);
        tn = sanitizeInputString(tn);
        cnam = sanitizeInputString(cnam);
        let error: string[] = [];

        if (!isValidNsnOrCc1(tn)) {
          error.push("Invalid Phone Number");
        }

        if (uploadItems.map((item) => item.phoneNumber).includes(tn)) {
          error.push("Duplicate phone number submitted in file");
        }

        if (cnam === "") {
          error.push(`Expected Number Name is required`);
        }

        if (cnam.length > 15) {
          error.push(`Expected Number Name must be 15 characters or less`);
        }

        if (error.length === 0) {
          uploadItems.push({ phoneNumber: tn, cnam });
        } else {
          errorUploadItems.push({
            phoneNumber: tn,
            cnam,
            errorMessage: error.join(", "),
            lineIndex: ind,
          });
        }
      });

      if (errorUploadItems.length === 0) {
        setFileUploadItems(uploadItems);
      }
      setFileUploadErrorItems(errorUploadItems);
      dispatch(numberNameActions.setIsLoading(false));
    } catch (e) {
      setFileUploadErrorItems([
        {
          phoneNumber: "",
          cnam: "",
          errorMessage: "There was an error parsing your file",
          lineIndex: 0,
        },
      ]);

      dispatch(numberNameActions.setIsLoading(false));
      return;
    } finally {
      if (fileInputRef.current && fileInputRef.current.files) {
        fileInputRef.current.value = ""; // Resetting the value. This is so if they select the same file twice, the onChange will still get triggered
      }
    }
  };

  return (
    <>
      <Box className="cnam-file-upload-header">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography variant="h6">Number Name File Upload</Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Maximum Number of TNs per file: {MAX_UPLOADED_TNS}
          </Typography>
        </Box>
      </Box>
      <Box className="cnam-file-upload-body">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6">1) Upload File</Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 3, color: "text.secondary" }}
                  >
                    First, input a CSV file you'd like to submit.
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    tabIndex={-1}
                    startIcon={<FaUpload />}
                    sx={{
                      height: "56px" /*The height of the textbox*/,
                      width: "100%",
                    }}
                  >
                    Upload CSV file
                    <VisuallyHiddenInput
                      type="file"
                      onInput={handleFileChange}
                      ref={fileInputRef}
                    />
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6">1a) Add Comment</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      mb: 3,
                    }}
                  >
                    (Optional) Add a comment
                  </Typography>
                  <TextField
                    fullWidth
                    label="Comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  ></TextField>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {/* <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}></Paper>
          </Grid> */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">2) Number Names to Submit</Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                After you input a file, the following TNs / Number Names will display
                over here for confirmation
              </Typography>

              {/*
               * ERROR DISPLAY
               */}
              {fileUploadErrorItems.length > 0 ? (
                <Paper sx={{ p: 2, backgroundColor: "#ffe28b" }}>
                  <Typography variant="h6">
                    Some of the TNs in file:{" "}
                    <span className="italic">{fileName}</span> seem to have
                    errors!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    Please correct lines and resubmit file
                  </Typography>
                  <TableContainer>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-head": {
                              backgroundColor: "#ffe28b",
                            },
                          }}
                        >
                          <TableCell>Line Index</TableCell>
                          <TableCell>Phone Number</TableCell>
                          <TableCell>Expected Number Name</TableCell>
                          <TableCell>Error Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fileUploadErrorItems.map((result, index) => (
                          <TableRow key={index}>
                            <TableCell>{result.lineIndex + 1}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>
                              {result.phoneNumber}
                            </TableCell>
                            <TableCell>{result.cnam || ""}</TableCell>
                            <TableCell>{result.errorMessage}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <>
                  {/*
                   * SUCCESS DISPLAY
                   */}
                  <TableContainer sx={{ maxHeight: "65vh" }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Phone Number</TableCell>
                          <TableCell>Expected Number Name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fileUploadItems.length === 0 && (
                          <>
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                className="italic"
                                variant="footer"
                              >
                                Upload a file to display entries...
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        {fileUploadItems.map((result, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontFamily: "monospace" }}>
                              {result.phoneNumber}
                            </TableCell>
                            <TableCell>{result.cnam}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const CHARS_TO_REMOVE = ['"', "+", "'"];

const sanitizeInputString = (input: string): string => {
  input = input.trim();
  for (const badChar of CHARS_TO_REMOVE) {
    input = input.replaceAll(badChar, "");
  }
  return input;
};
